<template>
  <div v-bind="$attrs" ref="containerRef">
    <slot name="activator" :attrs="attrs" />
  </div>
  <Teleport to="body">
    <fade-transition :duration="300">
      <div v-if="open" class="overlay">
        <div class="slider" ref="menuRef">
          <slot/>
        </div>
      </div>
    </fade-transition>
  </Teleport>
</template>

<script lang="ts" setup>

import { computed, defineProps, onUnmounted, ref, watch, defineEmits } from 'vue';
import { onClickOutside } from '@vueuse/core';
import FadeTransition from "@/components/Transitions/FadeTransition.vue";

const props = defineProps({
  activator: {
    type: Object as () => HTMLElement,
    required: false,
  },
  showMenu: {
    type: Boolean,
    default: false,
    required: false,
  },
  closeOnSelect: {
    type: Boolean,
    default: () => true,
  },
});

const containerRef = ref<HTMLElement>();
const open = ref(false);
const menuRef = ref<HTMLElement>();

onClickOutside(menuRef, () => {
  open.value = false;
});

const activatorRef = computed(() => props.activator || containerRef.value);
const emit = defineEmits(['open']);

function onClick() {
  if(props.closeOnSelect) {
    open.value = !open.value;
  } else {
    open.value = true;
  }
}

const attrs = computed(() => ({
  onClick,
}));

watch(activatorRef, (newValue, oldValue) => {
  newValue?.addEventListener('click', onClick);
  oldValue?.removeEventListener('click', onClick);
}, { immediate: true });

watch(menuRef, (newValue, oldValue) => {
  newValue?.addEventListener('click', onClick);
  oldValue?.removeEventListener('click', onClick);
}, { immediate: true });

onUnmounted(() => {
  activatorRef.value?.removeEventListener('click', onClick);
});

watch(() => open.value, (newValue) => emit('open', newValue));
watch(() => props.showMenu, (newValue) => open.value = newValue);
</script>

<style scoped>

.overlay {
  position: fixed; /* Sit on top of the page content */
  width: 100%; /* Full width (cover the whole page) */
  height: 100%; /* Full height (cover the whole page) */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0.5); /* Black background with opacity */
  z-index: 1000; /* Specify a stack order in case you're using a different order for other elements */
  cursor: pointer; /* Add a pointer on hover */
}

.slider {
  position: absolute;
  width: 100%;
  bottom: 0;
  overflow: hidden;
  transition: all ease-in 1s;
  border-bottom: 0;
  animation: slide 0.5s ease-in-out;
}

.slider.close {
  top: 50%;
  height: 0;
}

.fade-enter-active {
  animation: fade 0.3s ;
}

.fade-leave-active {
  animation: fade 0.3s reverse;
}

@keyframes fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@keyframes slide {
  0% {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(0);
  }
}
</style>
